import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useTemplateImages, type TemplateImage } from "@/hooks/useTemplateImages";
import { useProject } from "@/hooks/useProject";
import { Loader2, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";

// Conversión: 1 pulgada = 72 puntos
const POINTS_PER_INCH = 72;

const pointsToInches = (points: number): number => {
  return Math.round((points / POINTS_PER_INCH) * 100) / 100; // Round to 2 decimals
};

const inchesToPoints = (inches: number): number => {
  return Math.round(inches * POINTS_PER_INCH);
};

interface ImageConfigDialogProps {
  templateId: number;
  image: TemplateImage | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FormData {
  name: string;
  description: string;
  slide_number: number;
  position_x: number;
  position_y: number;
  width: number;
  height: number;
  image_type: "url" | "upload" | "client_logo" | "qr_code";
  fit_mode: "fixed" | "fit" | "fill";
  is_active: boolean;
  // Type-specific fields
  url?: string;
  qr_content?: string;
  qr_size?: number;
  qr_level?: "L" | "M" | "Q" | "H";
  client_field?: string;
}

export function ImageConfigDialog({ templateId, image, open, onOpenChange }: ImageConfigDialogProps) {
  const { createImage, updateImage, isCreating, isUpdating } = useTemplateImages(templateId);
  const { projectClient } = useProject();
  const [imageType, setImageType] = useState<"url" | "upload" | "client_logo" | "qr_code">("url");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedFilePath, setUploadedFilePath] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      name: "",
      description: "",
      slide_number: 1,
      position_x: 1.39, // ~100 puntos en pulgadas
      position_y: 1.39,
      width: 2.78,       // ~200 puntos en pulgadas
      height: 2.78,
      image_type: "url",
      fit_mode: "fit",
      is_active: true,
      url: "",
      qr_content: "",
      qr_size: 250,
      qr_level: "H",
      client_field: "logo",
    },
  });

  const watchImageType = watch("image_type");

  // Update form when image changes (edit mode)
  useEffect(() => {
    if (image) {
      reset({
        name: image.name,
        description: image.description || "",
        slide_number: image.slide_number,
        // Convertir puntos de la DB a pulgadas para la UI
        position_x: pointsToInches(image.position_x),
        position_y: pointsToInches(image.position_y),
        width: pointsToInches(image.width),
        height: pointsToInches(image.height),
        image_type: image.image_type,
        fit_mode: (image as any).fit_mode || "fit",
        is_active: image.is_active,
        url: image.image_config?.url || "",
        qr_content: image.image_config?.content || "",
        qr_size: image.image_config?.size || 250,
        qr_level: image.image_config?.level || "H",
        client_field: image.image_config?.field || "logo",
      });
      setImageType(image.image_type);
      if (image.image_config?.storage_path) {
        setUploadedFilePath(image.image_config.storage_path);
      }
    } else {
      reset({
        name: "",
        description: "",
        slide_number: 1,
        position_x: 1.39, // ~100 puntos en pulgadas
        position_y: 1.39,
        width: 2.78,      // ~200 puntos en pulgadas
        height: 2.78,
        image_type: "url",
        fit_mode: "fit",
        is_active: true,
        url: "",
        qr_content: "",
        qr_size: 250,
        qr_level: "H",
        client_field: "logo",
      });
      setImageType("url");
      setUploadedFile(null);
      setUploadedFilePath("");
    }
  }, [image, reset]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validar tipo de archivo
      const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/svg+xml"];
      if (!validTypes.includes(file.type)) {
        toast.error("Tipo de archivo no válido. Solo se permiten imágenes.");
        return;
      }

      // Validar tamaño (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("El archivo es demasiado grande. Máximo 5MB.");
        return;
      }

      setUploadedFile(file);
    }
  };

  const handleUploadFile = async () => {
    if (!uploadedFile) return null;

    setIsUploading(true);
    try {
      const projectId = projectClient.getProjectId();
      const fileExt = uploadedFile.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `${projectId}/${fileName}`;

      const { error: uploadError } = await projectClient.storage
        .from('template-images')
        .upload(filePath, uploadedFile);

      if (uploadError) {
        throw uploadError;
      }

      toast.success("Archivo subido correctamente");
      return filePath;
    } catch (error: any) {
      console.error("Error uploading file:", error);
      toast.error(`Error al subir archivo: ${error.message}`);
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveFile = async () => {
    if (uploadedFilePath) {
      try {
        await projectClient.storage
          .from('template-images')
          .remove([uploadedFilePath]);
        toast.success("Archivo eliminado");
      } catch (error: any) {
        console.error("Error removing file:", error);
      }
    }
    setUploadedFile(null);
    setUploadedFilePath("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const onSubmit = async (data: FormData) => {
    try {
      // Build image_config based on type
      let image_config: Record<string, any> = {};

      switch (data.image_type) {
        case "url":
          image_config = { url: data.url };
          break;
        case "upload":
          // Upload file if new file selected
          let storagePath = uploadedFilePath;
          if (uploadedFile) {
            const newPath = await handleUploadFile();
            if (!newPath) {
              toast.error("Error al subir el archivo");
              return;
            }
            storagePath = newPath;
          }

          if (!storagePath) {
            toast.error("Debes seleccionar un archivo");
            return;
          }

          image_config = {
            storage_path: storagePath,
            bucket: "template-images",
            original_name: uploadedFile?.name || image?.image_config?.original_name || ""
          };
          break;
        case "client_logo":
          image_config = { source: "client_logo", field: data.client_field || "logo" };
          break;
        case "qr_code":
          image_config = {
            content: data.qr_content || "",
            size: data.qr_size || 250,
            level: data.qr_level || "H"
          };
          break;
      }

      // Convertir pulgadas de la UI a puntos para la DB
      const payload = {
        template_id: templateId,
        name: data.name,
        description: data.description,
        slide_number: Number(data.slide_number),
        position_x: inchesToPoints(Number(data.position_x)),
        position_y: inchesToPoints(Number(data.position_y)),
        width: inchesToPoints(Number(data.width)),
        height: inchesToPoints(Number(data.height)),
        image_type: data.image_type,
        fit_mode: data.fit_mode,
        image_config,
        is_active: data.is_active,
      };

      if (image) {
        await updateImage({ id: image.id, ...payload });
      } else {
        await createImage(payload);
      }

      onOpenChange(false);
    } catch (error) {
      console.error("Error saving image:", error);
    }
  };

  const isSubmitting = isCreating || isUpdating;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{image ? "Editar Imagen" : "Agregar Imagen"}</DialogTitle>
          <DialogDescription>
            Configura la imagen que se insertará en el PowerPoint
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre *</Label>
              <Input
                id="name"
                {...register("name", { required: "El nombre es requerido" })}
                placeholder="Ej: Logo principal"
              />
              {errors.name && (
                <p className="text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                {...register("description")}
                placeholder="Descripción opcional de la imagen"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="image_type">Tipo de Imagen *</Label>
              <Select
                value={watchImageType}
                onValueChange={(value) => {
                  setValue("image_type", value as any);
                  setImageType(value as any);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="url">URL directa</SelectItem>
                  <SelectItem value="upload">Archivo subido</SelectItem>
                  <SelectItem value="client_logo">Logo del cliente</SelectItem>
                  <SelectItem value="qr_code">Código QR</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Type-specific configuration */}
          {imageType === "url" && (
            <div className="space-y-2 p-4 bg-muted/50 rounded-lg">
              <Label htmlFor="url">URL de la imagen *</Label>
              <Input
                id="url"
                {...register("url", {
                  required: imageType === "url" ? "La URL es requerida" : false,
                  pattern: {
                    value: /^https?:\/\/.+/,
                    message: "Debe ser una URL válida (http:// o https://)"
                  }
                })}
                placeholder="https://ejemplo.com/imagen.png"
              />
              {errors.url && (
                <p className="text-sm text-red-600">{errors.url.message}</p>
              )}
              <p className="text-xs text-muted-foreground">
                La imagen se descargará desde esta URL al generar el reporte
              </p>
            </div>
          )}

          {imageType === "upload" && (
            <div className="space-y-2 p-4 bg-muted/50 rounded-lg">
              <Label htmlFor="file">Archivo de imagen *</Label>
              <div className="space-y-2">
                <Input
                  id="file"
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                  onChange={handleFileChange}
                  disabled={isUploading}
                />
                {(uploadedFile || uploadedFilePath) && (
                  <div className="flex items-center gap-2 p-2 bg-background rounded border">
                    <Upload className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm flex-1">
                      {uploadedFile?.name || uploadedFilePath.split('/').pop() || "Archivo subido"}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveFile}
                      disabled={isUploading}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Tamaño máximo: 5MB. Formatos: JPG, PNG, GIF, WebP, SVG
              </p>
            </div>
          )}

          {imageType === "client_logo" && (
            <div className="space-y-2 p-4 bg-muted/50 rounded-lg">
              <Label htmlFor="client_field">Campo del cliente *</Label>
              <Select
                value={watch("client_field") || "logo"}
                onValueChange={(value) => setValue("client_field", value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="logo">Logo</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Se utilizará el logo del cliente asociado al lanzamiento
              </p>
            </div>
          )}

          {imageType === "qr_code" && (
            <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
              <div className="space-y-2">
                <Label htmlFor="qr_content">Contenido del QR *</Label>
                <Textarea
                  id="qr_content"
                  {...register("qr_content", {
                    required: imageType === "qr_code" ? "El contenido es requerido" : false,
                  })}
                  placeholder="https://ejemplo.com o cualquier texto"
                  rows={3}
                />
                {errors.qr_content && (
                  <p className="text-sm text-red-600">{errors.qr_content.message}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="qr_size">Tamaño (px)</Label>
                  <Input
                    id="qr_size"
                    type="number"
                    min="100"
                    max="1000"
                    {...register("qr_size")}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="qr_level">Nivel de corrección</Label>
                  <Select
                    value={watch("qr_level") || "H"}
                    onValueChange={(value) => setValue("qr_level", value as any)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="L">Bajo (L)</SelectItem>
                      <SelectItem value="M">Medio (M)</SelectItem>
                      <SelectItem value="Q">Alto (Q)</SelectItem>
                      <SelectItem value="H">Muy Alto (H)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                El código QR se generará automáticamente al crear el reporte
              </p>
            </div>
          )}

          {/* Position and Size */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">Posición y Tamaño</h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="slide_number">Número de Slide *</Label>
                <Input
                  id="slide_number"
                  type="number"
                  min="1"
                  {...register("slide_number", {
                    required: "El número de slide es requerido",
                    min: { value: 1, message: "Debe ser mayor a 0" }
                  })}
                />
                {errors.slide_number && (
                  <p className="text-sm text-red-600">{errors.slide_number.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="is_active">Estado</Label>
                <div className="flex items-center space-x-2 h-10">
                  <Switch
                    id="is_active"
                    checked={watch("is_active")}
                    onCheckedChange={(checked) => setValue("is_active", checked)}
                  />
                  <Label htmlFor="is_active" className="text-sm text-muted-foreground cursor-pointer">
                    {watch("is_active") ? "Activa" : "Inactiva"}
                  </Label>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="position_x">Posición X (pulgadas) *</Label>
                <Input
                  id="position_x"
                  type="number"
                  step="0.01"
                  {...register("position_x", { required: "La posición X es requerida" })}
                />
                {errors.position_x && (
                  <p className="text-sm text-red-600">{errors.position_x.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="position_y">Posición Y (pulgadas) *</Label>
                <Input
                  id="position_y"
                  type="number"
                  step="0.01"
                  {...register("position_y", { required: "La posición Y es requerida" })}
                />
                {errors.position_y && (
                  <p className="text-sm text-red-600">{errors.position_y.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="width">Ancho (pulgadas) *</Label>
                <Input
                  id="width"
                  type="number"
                  step="0.01"
                  min="1"
                  {...register("width", {
                    required: "El ancho es requerido",
                    min: { value: 1, message: "Debe ser mayor a 0" }
                  })}
                />
                {errors.width && (
                  <p className="text-sm text-red-600">{errors.width.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="height">Alto (pulgadas) *</Label>
                <Input
                  id="height"
                  type="number"
                  step="0.01"
                  min="1"
                  {...register("height", {
                    required: "El alto es requerido",
                    min: { value: 1, message: "Debe ser mayor a 0" }
                  })}
                />
                {errors.height && (
                  <p className="text-sm text-red-600">{errors.height.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-2 pt-2 border-t">
              <Label htmlFor="fit_mode">Modo de ajuste *</Label>
              <Select
                value={watch("fit_mode") || "fit"}
                onValueChange={(value) => setValue("fit_mode", value as any)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">
                    <div className="flex flex-col">
                      <span className="font-medium">Fijo (Fixed)</span>
                      <span className="text-xs text-muted-foreground">
                        Usa el ancho/alto exacto (puede deformar)
                      </span>
                    </div>
                  </SelectItem>
                  <SelectItem value="fit">
                    <div className="flex flex-col">
                      <span className="font-medium">Ajustar (Fit) ⭐</span>
                      <span className="text-xs text-muted-foreground">
                        Ajusta sin deformar, respetando proporciones
                      </span>
                    </div>
                  </SelectItem>
                  <SelectItem value="fill">
                    <div className="flex flex-col">
                      <span className="font-medium">Rellenar (Fill)</span>
                      <span className="text-xs text-muted-foreground">
                        Llena el espacio completo (puede cortar)
                      </span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                <strong>Recomendado para logos:</strong> Usar "Ajustar (Fit)" para evitar deformaciones.
                El ancho/alto configurado será el tamaño máximo.
              </p>
            </div>

            <p className="text-xs text-muted-foreground">
              Las medidas se muestran en pulgadas. Se convertirán automáticamente a puntos y luego a EMUs al generar el PowerPoint.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {image ? "Actualizar" : "Crear"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
